import { defineStore } from 'pinia'
import { add } from 'date-fns'
import type { PermissionEnum, User } from '~~/models/graphql/generated/types'
import * as Sentry from '@sentry/browser'

export interface UserSessionState {
  id?: string
  email?: string
  name?: string
  avatarUrl?: string
  permissions: string[]
  roles: string[]
  loading: boolean
  userDataLoaded: boolean
  getDataPromise: Promise<void> | null
  twoFactorEnabled: boolean
  trainingInScope: boolean
  sessionExpires: Date | undefined
}

const initialState: UserSessionState = {
  id: undefined,
  email: undefined,
  name: undefined,
  avatarUrl: undefined,
  permissions: [],
  roles: [],
  loading: true,
  userDataLoaded: false,
  getDataPromise: null,
  twoFactorEnabled: false,
  trainingInScope: false,
  sessionExpires: undefined,
}

export const useUserSessionStore = defineStore('user-session-store', {
  state: (): UserSessionState => ({ ...initialState }),
  actions: {
    async ensureSessionLoaded() {
      // let the getDataPromise resolve if it has already been called
      if (this.userDataLoaded === false && this.loading === true) {
        await this.getDataPromise
      }

      // try loading the data if it hasn't yet been loaded
      if (this.userDataLoaded === false) {
        await this.getData()
      }
    },
    getData() {
      this.loading = true
      this.getDataPromise = useBffClient()
        .getSessionInfo()
        .then((data) => {
          this.setData(data)
          this.userDataLoaded = true
        })
        .catch((e) => {
          Sentry.captureException(e)
          console.error(e)
        })
        .finally(() => {
          this.loading = false
          this.getDataPromise = null
        })
      return this.getDataPromise
    },
    setData(data: User) {
      this.id = data.id
      this.email = data.email
      this.avatarUrl = data.avatarUrl ?? undefined
      const name = `${data?.firstName ?? ''} ${data?.surname ?? ''}`.trim()
      this.name =
        name.replace(/ /g, '').length === 0 ? `#User${data?.id}` : name
      this.twoFactorEnabled = data.twoFactorEnabled
      this.permissions = data.permissions
      this.roles = data.roles
      this.trainingInScope = data.trainingInScope
    },
    setSessionSecondsRemaining(secondsRemaining: number): void {
      this.sessionExpires = add(new Date(), { seconds: secondsRemaining })
    },
  },
  getters: {
    userHasPermission: (state) => {
      return (permission: PermissionEnum) =>
        state.permissions.includes(permission)
    },
    userHasRole: (state) => {
      return (role: string) => state.roles.includes(role)
    },
  },
})
